import { t } from '../i18n';
// client/admin-client/src/pages/TableManagement.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import GameList from '../components/GameList';
import InfoPresetBuilder, { defaultInfoDraft, buildPresetRaw, buildTimePayload, buildInfoTargetPayload, isActiveAt, parsePresetRaw, formatTimeHuman, validateInfoTimerDraft, TARGET_LANGUAGE_OPTIONS, normalizeInfoTargetDevice, normalizeInfoTargetLanguage, normalizeInfoTargetCountry, } from '../components/InfoPresetBuilder';
function getAuthHeaders() {
    const token = localStorage.getItem('token') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
}
function nowLocalInput() {
    const d = new Date();
    d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// [ADD][ANCHOR:HELPER:YMD]
function toYmdLocal(d) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
// [ADD][ANCHOR:HELPER:YMD_TO_LOCAL_INPUT]
function ymdToFilterLocalInput(ymd) {
    // 날짜 버튼 클릭 시 정오로 세팅(안전)
    return `${ymd}T12:00`;
}
// [ADD][ANCHOR:HELPER:ADD_DAYS]
function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}
// [ADD][ANCHOR:ADMIN_UI:I18N_HELPERS]
// JSONB(i18n-map) 또는 string을 안전하게 "표시용 문자열"로 변환
function normalizeLangTag(raw) {
    let s = String(raw || '').trim();
    if (!s)
        return 'en';
    s = s.replace(/_/g, '-').toLowerCase();
    if (s === 'kr')
        return 'ko';
    if (s.startsWith('ko'))
        return 'ko';
    return s;
}
function toI18nText(v, lang = 'ko') {
    if (v == null)
        return '';
    if (typeof v === 'string')
        return v;
    if (typeof v === 'number')
        return String(v);
    if (typeof v === 'object' && !Array.isArray(v)) {
        const l = normalizeLangTag(lang);
        const base = l.split('-')[0];
        const candidates = [l];
        if (base && base !== l)
            candidates.push(base);
        if (l === 'ko')
            candidates.push('kr');
        candidates.push('en');
        for (const k of candidates) {
            const val = v?.[k];
            if (typeof val === 'string' && val.trim())
                return val.trim();
            if (typeof val === 'number')
                return String(val);
        }
        try {
            return JSON.stringify(v);
        }
        catch {
            return String(v);
        }
    }
    return String(v);
}
// 저장용: 기존 i18n-map이 있으면 유지하면서 ko만 업데이트
function mergeKoI18n(existing, koText) {
    const t = (koText ?? '').toString().trim();
    if (!t)
        return null;
    const base = (existing && typeof existing === 'object' && !Array.isArray(existing))
        ? existing
        : {};
    return { ...base, ko: t };
}
function isValidPercent2(v) {
    if (v === '' || v === null || v === undefined)
        return true;
    const raw = String(v).trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(raw))
        return false;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n <= 100;
}
// SB/BB는 0.25, 0.50 같은 소수 블라인드를 허용한다.
// DECIMAL 컬럼에 안전하게 저장되도록 문자열 소수 2자리로 정규화한다.
function normalizeBlindInput(value) {
    if (value === '' || value === null || value === undefined)
        return null;
    const raw = String(value).trim().replace(',', '.');
    if (!/^\d+(?:\.\d{1,2})?$/.test(raw))
        return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return n.toFixed(2);
}
function isValidBlindInput(value) {
    return normalizeBlindInput(value) !== null;
}
const DEFAULT_FORM = {
    name: '',
    small_blind: 1,
    big_blind: 2,
    default_game: 'NLH',
    max_players: 9,
    max_count: 10,
    lock: 'UNLOCK',
    password: '',
    rake_percent: 0,
    rake_cap_bb: 0,
    event_fee_percent: 0,
    lounge_point_percent: '0.00',
    // ✅ display_sets(table_group)용 추가
    highlight_point: '',
    highlight_message: '',
    group_time_mode: 'infinite', // infinite | range
    group_start_local: '',
    group_end_local: '',
    group_refresh_days: 30,
    // ✅ 그룹 생성시 같이 만들 info buttons
    info_buttons: [],
};
export default function TableManagement() {
    const [tableGroups, setTableGroups] = useState([]);
    const [displayMetaByCode, setDisplayMetaByCode] = useState({}); // { [code]: table_group row }
    const [displaySetsByCode, setDisplaySetsByCode] = useState({}); // { [display_code]: display_sets[] }
    const [standaloneDisplayCodes, setStandaloneDisplayCodes] = useState([]); // TableList 없이 information_button만 가진 코드
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState(DEFAULT_FORM);
    // [ADD][ANCHOR:TM:STATE_INFO_PRESET_EDIT]
    const [infoEditOpen, setInfoEditOpen] = useState(false);
    const [infoEditTarget, setInfoEditTarget] = useState(null); // { code, groupName, id }
    const [infoEditDraft, setInfoEditDraft] = useState(defaultInfoDraft());
    const [infoEditSaving, setInfoEditSaving] = useState(false);
    // [ADD][ANCHOR:TM:HELPER_INFO_PRESET_EDIT]
    function normalizeTimeObj(t) {
        if (!t)
            return null;
        if (typeof t === 'object')
            return t;
        if (typeof t === 'string') {
            try {
                return JSON.parse(t);
            }
            catch {
                return null;
            }
        }
        return null;
    }
    function utcIsoToLocalInput(iso) {
        if (!iso)
            return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime()))
            return '';
        d.setSeconds(0, 0);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    // [ADD][ANCHOR:TM:FN_OPEN_STANDALONE_INFO_CREATE]
    function openStandaloneInfoCreate() {
        setInfoEditTarget({
            mode: 'standalone-create',
            code: '',
            groupName: t('단독 인포버튼'),
            id: null,
            hp: null,
            hm: null,
        });
        setInfoEditDraft(defaultInfoDraft());
        setInfoEditOpen(true);
        setError('');
    }

    // [ADD][ANCHOR:TM:FN_OPEN_INFO_PRESET_EDIT]
    function openInfoPresetEdit(code, groupName, row) {
        const t = normalizeTimeObj(row?.time) || { mode: 'infinite', refresh_days: 30 };
        const preset = parsePresetRaw(row?.preset || '');
        const next = {
            ...defaultInfoDraft(),
            ...preset,
            // ✅ JSONB -> string (input 안전)
            highlight_point: toI18nText(row?.highlight_point, 'ko') || '',
            highlight_message: toI18nText(row?.highlight_message, 'ko') || '',
            status: row?.status || 'active',
            device: row?.device || 'all',
            language: row?.language || 'all',
            country: row?.country || 'ALL',
            priority: row?.priority ?? 0,
            time_mode: t?.mode === 'range' ? 'range' : 'infinite',
            refresh_days: Number(t?.refresh_days || 30),
            start_local: t?.start_utc ? utcIsoToLocalInput(t.start_utc) : '',
            end_local: t?.end_utc ? utcIsoToLocalInput(t.end_utc) : '',
        };
        // ✅ 기존 번역맵 유지 위해 원본도 같이 저장
        setInfoEditTarget({
            mode: 'edit',
            code,
            groupName: toI18nText(groupName, 'ko') || String(code),
            id: row?.id,
            hp: row?.highlight_point ?? null,
            hm: row?.highlight_message ?? null,
        });
        setInfoEditDraft(next);
        setInfoEditOpen(true);
    }
    // [ADD][ANCHOR:TM:FN_SAVE_INFO_PRESET_EDIT]
    async function saveInfoPresetEdit() {
        const standaloneCreate = infoEditTarget?.mode === 'standalone-create';
        if (!standaloneCreate && (!infoEditTarget?.code || !infoEditTarget?.id))
            return;
        const timerValidation = validateInfoTimerDraft(infoEditDraft);
        if (!timerValidation.ok) {
            setError(timerValidation.message || t("Due Date를 입력해야 합니다."));
            return;
        }
        const time = buildTimePayload(infoEditDraft);
        if (!time) {
            setError(t("인포 버튼 시간 설정이 올바르지 않습니다."));
            return;
        }
        setInfoEditSaving(true);
        setError('');
        try {
            const payload = {
                highlight_point: mergeKoI18n(infoEditTarget?.hp, infoEditDraft.highlight_point),
                highlight_message: mergeKoI18n(infoEditTarget?.hm, infoEditDraft.highlight_message),
                status: infoEditDraft.status || 'active',
                time,
                preset: buildPresetRaw(infoEditDraft),
                ...buildInfoTargetPayload(infoEditDraft),
            };

            if (standaloneCreate) {
                const res = await axios.post('/admin/table/info', payload, { headers: getAuthHeaders() });
                const displayCode = String(res?.data?.display_code || res?.data?.item?.display_code || '').trim();
                const created = res?.data?.item || res?.data?.displaySet?.information_buttons?.[0] || null;
                await fetchGroups();
                if (displayCode && created) openInfoPresetEdit(displayCode, t('단독 인포버튼'), created);
                else {
                    setInfoEditOpen(false);
                    setInfoEditTarget(null);
                }
            }
            else {
                await axios.patch(`/admin/table/group/${infoEditTarget.code}/display-sets/${infoEditTarget.id}`, payload, { headers: getAuthHeaders() });
                setInfoEditOpen(false);
                setInfoEditTarget(null);
                await fetchGroups();
            }
        }
        catch (e) {
            setError(e?.response?.data?.message || (standaloneCreate ? t('단독 인포버튼 생성 실패') : t('인포 버튼 프리셋 수정 실패')));
        }
        finally {
            setInfoEditSaving(false);
        }
    }
    // [ADD][ANCHOR:TM:DELETE_INFO_CARD]
    async function deleteInfoCard(code, row, groupName = '') {
        const displayCode = String(code || '').trim();
        const id = Number(row?.id);
        if (!displayCode || !Number.isFinite(id) || id <= 0)
            return;
        const title = toI18nText(row?.highlight_point, 'ko') || t('인포카드');
        const ok = window.confirm(`${groupName || displayCode} · ${title}\n\n${t('이 인포카드를 삭제하시겠습니까? 같은 카드의 데스크톱/모바일·언어별 변형도 함께 삭제됩니다. 테이블 그룹과 게임 테이블은 삭제되지 않습니다.')}`);
        if (!ok)
            return;
        setInfoEditSaving(true);
        setError('');
        try {
            await axios.delete(`/admin/table/group/${displayCode}/info/${id}`, { headers: getAuthHeaders() });
            if (infoEditTarget?.code === displayCode) {
                setInfoEditOpen(false);
                setInfoEditTarget(null);
            }
            await fetchGroups();
        }
        catch (e) {
            setError(e?.response?.data?.message || t('인포카드 삭제 실패'));
        }
        finally {
            setInfoEditSaving(false);
        }
    }
    // [ADD][ANCHOR:TM:INFO_TARGET_VARIANT_MATRIX]
    const infoVariantDevices = [
        { value: 'desktop', label: t('데스크톱') },
        { value: 'mobile', label: t('모바일') },
    ];
    const infoVariantLanguages = TARGET_LANGUAGE_OPTIONS.filter((opt) => opt.value !== 'all');
    function getInfoRowsByCurrentCode() {
        const code = infoEditTarget?.code;
        if (!code)
            return [];
        const items = Array.isArray(displaySetsByCode?.[code]) ? displaySetsByCode[code] : [];
        return items.filter((x) => x?.type === 'information_button');
    }
    function findExactInfoVariant(device, language) {
        const nd = normalizeInfoTargetDevice(device);
        const nl = normalizeInfoTargetLanguage(language);
        return getInfoRowsByCurrentCode().find((row) => normalizeInfoTargetDevice(row?.device) === nd &&
            normalizeInfoTargetLanguage(row?.language) === nl &&
            normalizeInfoTargetCountry(row?.country) === 'ALL');
    }
    function getInfoVariantBgLabel(row) {
        if (!row)
            return t('미생성');
        const parsed = parsePresetRaw(row?.preset || '');
        if (parsed.design_enabled && parsed.design_image_url)
            return t('디자인 이미지');
        if (parsed.design_enabled)
            return t('디자인');
        if (parsed.bg_mode === 'image' && parsed.bg_image_url)
            return t('이미지');
        if (parsed.bg_mode === 'color')
            return parsed.bg_color || t('색상');
        return t('배경 없음');
    }
    async function createInfoTargetVariantFromCurrent(device, language) {
        if (!infoEditTarget?.code)
            return;
        const existed = findExactInfoVariant(device, language);
        if (existed) {
            openInfoPresetEdit(infoEditTarget.code, infoEditTarget.groupName, existed);
            return;
        }
        const timerValidation = validateInfoTimerDraft(infoEditDraft);
        if (!timerValidation.ok) {
            setError(timerValidation.message || t("Due Date를 입력해야 합니다."));
            return;
        }
        const time = buildTimePayload(infoEditDraft);
        if (!time) {
            setError(t("인포 버튼 시간 설정이 올바르지 않습니다."));
            return;
        }
        const nextDraft = {
            ...infoEditDraft,
            device: normalizeInfoTargetDevice(device),
            language: normalizeInfoTargetLanguage(language),
            country: 'ALL',
        };
        setInfoEditSaving(true);
        setError('');
        try {
            const res = await axios.post(`/admin/table/group/${infoEditTarget.code}/info`, {
                highlight_point: mergeKoI18n(null, nextDraft.highlight_point),
                highlight_message: mergeKoI18n(null, nextDraft.highlight_message),
                status: nextDraft.status || 'active',
                time,
                preset: buildPresetRaw(nextDraft),
                ...buildInfoTargetPayload(nextDraft),
            }, { headers: getAuthHeaders() });
            const created = res?.data?.item || res?.data?.displaySet?.information_buttons?.[0] || null;
            await fetchGroups();
            if (created)
                openInfoPresetEdit(infoEditTarget.code, infoEditTarget.groupName, created);
        }
        catch (e) {
            setError(e?.response?.data?.message || t("타겟별 인포 버튼 생성 실패"));
        }
        finally {
            setInfoEditSaving(false);
        }
    }
    function renderInfoTargetVariantMatrix() {
        if (!infoEditOpen || !infoEditTarget?.code)
            return null;
        const currentDevice = normalizeInfoTargetDevice(infoEditDraft.device);
        const currentLanguage = normalizeInfoTargetLanguage(infoEditDraft.language);
        return (<div className="mb-4 border rounded p-3 bg-amber-50/60">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold">{t("디바이스/언어별 배경 빠른 관리")}</div>
                <div className="text-xs text-gray-600 mt-1">{t("각 칸은 별도 인포 버튼 항목입니다. 없으면 현재 설정을 복제해서 생성하고, 있으면 해당 항목을 열어 배경 이미지를 따로 바꿀 수 있습니다.")}</div>
              </div>
              <div className="text-xs text-gray-600">
                {t("현재 편집:")} {currentDevice} / {currentLanguage}
              </div>
            </div>

            <div className="mt-3 space-y-3">
              {infoVariantDevices.map((dev) => (<div key={dev.value} className="border rounded bg-white p-2">
                  <div className="font-medium mb-2">{dev.label}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    {infoVariantLanguages.map((lang) => {
                        const row = findExactInfoVariant(dev.value, lang.value);
                        const isCurrent = currentDevice === dev.value && currentLanguage === lang.value;
                        return (<button key={`${dev.value}-${lang.value}`} type="button" disabled={infoEditSaving} onClick={() => row
                                ? openInfoPresetEdit(infoEditTarget.code, infoEditTarget.groupName, row)
                                : createInfoTargetVariantFromCurrent(dev.value, lang.value)} className={[
                                "text-left rounded border px-2 py-2 hover:bg-gray-50 disabled:opacity-60",
                                row ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white",
                                isCurrent ? "ring-2 ring-gray-900" : "",
                            ].join(' ')} title={row ? t("기존 타겟 항목 열기") : t("현재 설정으로 타겟 항목 생성")}>
                            <div className="font-semibold text-sm">{lang.label}</div>
                            <div className="text-xs text-gray-600 mt-1">{row ? t("기존") : t("생성")}</div>
                            <div className="text-xs text-gray-500 truncate">{getInfoVariantBgLabel(row)}</div>
                          </button>);
                    })}
                  </div>
                </div>))}
            </div>
          </div>);
    }

    // ✅ 달력+시간 필터
    const [filterAtLocal, setFilterAtLocal] = useState(nowLocalInput());
    // [ADD][ANCHOR:STATE:QUICK_DAY]
    const [quickDayYmd, setQuickDayYmd] = useState(''); // 'YYYY-MM-DD'
    const filterAtDate = useMemo(() => {
        if (!filterAtLocal)
            return null;
        const d = new Date(filterAtLocal);
        if (Number.isNaN(d.getTime()))
            return null;
        return d;
    }, [filterAtLocal]);
    const canSubmit = useMemo(() => {
        if (!form.name?.trim())
            return false;
        if (!isValidBlindInput(form.small_blind))
            return false;
        if (!isValidBlindInput(form.big_blind))
            return false;
        if (!Number.isFinite(Number(form.max_players)))
            return false;
        if (!Number.isFinite(Number(form.max_count)))
            return false;
        if (!Number.isFinite(Number(form.rake_percent)) || Number(form.rake_percent) < 0 || Number(form.rake_percent) > 100)
            return false;
        if (!Number.isFinite(Number(form.rake_cap_bb)) || Number(form.rake_cap_bb) < 0)
            return false;
        if (!Number.isFinite(Number(form.event_fee_percent)) || Number(form.event_fee_percent) < 0 || Number(form.event_fee_percent) > 100)
            return false;
        if (!isValidPercent2(form.lounge_point_percent))
            return false;
        if (form.lock === 'LOCK' && !form.password)
            return false;
        // 그룹 time 검증(기간이면 start/end 필수)
        if (form.group_time_mode === 'range') {
            if (!form.group_start_local || !form.group_end_local)
                return false;
            const s = new Date(form.group_start_local);
            const e = new Date(form.group_end_local);
            if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
                return false;
            if (s.getTime() >= e.getTime())
                return false;
        }
        // info_buttons element max 4 검증
        for (const b of form.info_buttons || []) {
            const els = Array.isArray(b.elements) ? b.elements : [];
            if (els.length > 4)
                return false;
            const timerValidation = validateInfoTimerDraft(b);
            if (!timerValidation.ok)
                return false;
            if (b.time_mode === 'range') {
                if (!b.start_local || !b.end_local)
                    return false;
                const s = new Date(b.start_local);
                const e = new Date(b.end_local);
                if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
                    return false;
                if (s.getTime() >= e.getTime())
                    return false;
            }
        }
        return true;
    }, [form]);
    // [PATCH][ANCHOR:FETCH_DISPLAY_META_STORE_ALL_ITEMS]
    async function fetchDisplayMeta(codes) {
        const nextMeta = {};
        const nextSets = {};
        await Promise.all(codes.map(async (code) => {
            try {
                const res = await axios.get(`/admin/table/group/${code}/display-sets`, {
                    headers: getAuthHeaders(),
                });
                const items = res.data?.items || [];
                const groupRow = items.find((x) => x.type === 'table_group') || null;
                nextMeta[code] = groupRow;
                nextSets[code] = items;
            }
            catch {
                nextMeta[code] = null;
                nextSets[code] = [];
            }
        }));
        setDisplayMetaByCode(nextMeta);
        setDisplaySetsByCode(nextSets);
    }
    async function fetchGroups() {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get('/admin/table/group', { headers: getAuthHeaders() });
            const raw = res?.data;
            const groups = (Array.isArray(raw) ? raw : null) ||
                raw?.tableGroups ||
                raw?.items ||
                raw?.groups ||
                raw?.data?.tableGroups ||
                raw?.data?.items ||
                raw?.data?.groups ||
                [];
            setTableGroups(groups);
            const standaloneCodes = (raw?.standaloneDisplayCodes || raw?.standalone_display_codes || [])
                .map((code) => String(code || '').trim())
                .filter(Boolean);
            setStandaloneDisplayCodes(standaloneCodes);
            const codes = Array.from(new Set([
                ...groups.map((g) => g.display_code).filter(Boolean),
                ...standaloneCodes,
            ]));
            await fetchDisplayMeta(codes);
        }
        catch (e) {
            setError(e?.response?.data?.message || t("\uD14C\uC774\uBE14 \uADF8\uB8F9 \uBAA9\uB85D \uC870\uD68C \uC2E4\uD328"));
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        fetchGroups();
    }, []);
    function addInfoButton() {
        setForm((p) => ({
            ...p,
            info_buttons: [...(p.info_buttons || []), defaultInfoDraft()],
        }));
    }
    function updateInfoButton(idx, nextDraft) {
        setForm((p) => ({
            ...p,
            info_buttons: (p.info_buttons || []).map((b, i) => (i === idx ? nextDraft : b)),
        }));
    }
    function removeInfoButton(idx) {
        setForm((p) => ({
            ...p,
            info_buttons: (p.info_buttons || []).filter((_, i) => i !== idx),
        }));
    }
    async function onCreateGroup(e) {
        e.preventDefault();
        for (const b of form.info_buttons || []) {
            const timerValidation = validateInfoTimerDraft(b);
            if (!timerValidation.ok) {
                setError(timerValidation.message || t("Due Date\uB97C \uC785\uB825\uD574\uC57C \uD569\uB2C8\uB2E4."));
                return;
            }
        }
        if (!canSubmit) {
            setError(t("\uC785\uB825\uAC12\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."));
            return;
        }
        setCreating(true);
        setError('');
        try {
            // ✅ display_set(table_group) payload
            const groupTimeDraft = {
                time_mode: form.group_time_mode,
                start_local: form.group_start_local,
                end_local: form.group_end_local,
                refresh_days: form.group_refresh_days,
            };
            const timePayload = buildTimePayload(groupTimeDraft);
            if (!timePayload) {
                setError(t("\uADF8\uB8F9 \uC2DC\uAC04 \uC124\uC815\uC774 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."));
                setCreating(false);
                return;
            }
            for (const b of form.info_buttons || []) {
                const timerValidation = validateInfoTimerDraft(b);
                if (!timerValidation.ok) {
                    setError(timerValidation.message || t("Due Date\uB97C \uC785\uB825\uD574\uC57C \uD569\uB2C8\uB2E4."));
                    setCreating(false);
                    return;
                }
            }
            const normalizedSmallBlind = normalizeBlindInput(form.small_blind);
            const normalizedBigBlind = normalizeBlindInput(form.big_blind);
            if (!normalizedSmallBlind || !normalizedBigBlind) {
                setError(t("SB/BB\uB294 0\uBCF4\uB2E4 \uD070 \uC22B\uC790\uC774\uBA70 \uC18C\uC218\uC810\uC740 \uCD5C\uB300 2\uC790\uB9AC\uAE4C\uC9C0 \uC785\uB825\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4. \uC608: 0.25 / 0.50"));
                setCreating(false);
                return;
            }
            const payload = {
                name: { ko: form.name.trim() },
                small_blind: normalizedSmallBlind,
                big_blind: normalizedBigBlind,
                default_game: form.default_game,
                max_players: Number(form.max_players),
                max_count: Number(form.max_count),
                lock: form.lock,
                password: form.lock === 'LOCK' ? form.password : null,
                rake_percent: Number(form.rake_percent),
                rake_cap_bb: Number(form.rake_cap_bb) || 0,
                event_fee_percent: Number(form.event_fee_percent) || 0,
                lounge_point_percent: form.lounge_point_percent === '' ? '0.00' : String(form.lounge_point_percent),
                // ✅ 새로 추가
                display_set: {
                    // ✅ DisplaySet.highlight_* is JSONB
                    highlight_point: mergeKoI18n(null, form.highlight_point),
                    highlight_message: mergeKoI18n(null, form.highlight_message),
                    time: timePayload,
                    status: 'active',
                },
                // ✅ 인포 버튼들
                info_buttons: (form.info_buttons || []).map((b) => ({
                    highlight_point: mergeKoI18n(null, b.highlight_point),
                    highlight_message: mergeKoI18n(null, b.highlight_message),
                    time: buildTimePayload(b),
                    status: b.status || 'active',
                    preset: buildPresetRaw(b), // 콤마 문자열
                    ...buildInfoTargetPayload(b),
                })),
            };
            const res = await axios.post('/admin/table/group', payload, { headers: getAuthHeaders() });
            const created = res.data?.tableGroup;
            setForm(DEFAULT_FORM);
            await fetchGroups();
        }
        catch (e2) {
            setError(e2?.response?.data?.message || t("\uD14C\uC774\uBE14 \uADF8\uB8F9 \uC0DD\uC131 \uC2E4\uD328"));
        }
        finally {
            setCreating(false);
        }
    }
    const filteredGroups = useMemo(() => {
        const groups = tableGroups || [];
        if (!filterAtDate)
            return groups;
        return groups.filter((g) => {
            const code = g.display_code;
            const items = Array.isArray(displaySetsByCode?.[code]) ? displaySetsByCode[code] : [];
            const groupRow = items.find((x) => x.type === 'table_group') || displayMetaByCode?.[code] || null;
            const infoRows = items.filter((x) => x.type === 'information_button');
            const groupActive = isEnabledRow(groupRow) && isActiveAt(groupRow?.time, filterAtDate);
            const infoActive = infoRows.some((r) => isEnabledRow(r) && isActiveAt(r?.time, filterAtDate));
            return groupActive || infoActive;
        });
    }, [tableGroups, displayMetaByCode, displaySetsByCode, filterAtDate]);
    // [ADD][ANCHOR:MEMO:DISPLAY_INDEX_BY_CODE]
    const displayIndexByCode = useMemo(() => {
        const out = {};
        for (const [code, items] of Object.entries(displaySetsByCode || {})) {
            const list = Array.isArray(items) ? items : [];
            out[code] = {
                groupRow: list.find((x) => x.type === 'table_group') || null,
                infoRows: list.filter((x) => x.type === 'information_button'),
            };
        }
        return out;
    }, [displaySetsByCode]);
    // [ADD][ANCHOR:HELPER:IS_ENABLED_ROW]
    function isEnabledRow(row) {
        if (!row)
            return false;
        const st = String(row.status || 'active');
        return st !== 'disabled';
    }
    // [ADD][ANCHOR:HELPER:IS_RANGE_TIME]
    function isRangeTime(t) {
        if (!t)
            return false;
        return String(t.mode || '') === 'range' && !!t.start_utc && !!t.end_utc;
    }
    // [ADD][ANCHOR:MEMO:DAY_BUTTONS]
    const dayButtons = useMemo(() => {
        const base = filterAtDate || new Date();
        const windowStart = addDays(new Date(base), -15);
        const windowEnd = addDays(new Date(base), 45);
        windowStart.setHours(12, 0, 0, 0);
        windowEnd.setHours(12, 0, 0, 0);
        const days = [];
        for (let d = new Date(windowStart); d.getTime() <= windowEnd.getTime(); d = addDays(d, 1)) {
            let gCount = 0;
            let iCount = 0;
            for (const code of Object.keys(displayIndexByCode)) {
                const idx = displayIndexByCode[code];
                const gr = idx?.groupRow;
                // ✅ 날짜 버튼은 range 항목만 집계 (infinite는 '상시'로 따로)
                if (isEnabledRow(gr) && isRangeTime(gr?.time) && isActiveAt(gr?.time, d))
                    gCount += 1;
                const infos = Array.isArray(idx?.infoRows) ? idx.infoRows : [];
                for (const r of infos) {
                    if (isEnabledRow(r) && isRangeTime(r?.time) && isActiveAt(r?.time, d))
                        iCount += 1;
                }
            }
            if (gCount > 0 || iCount > 0) {
                const ymd = toYmdLocal(d);
                days.push({ ymd, gCount, iCount });
            }
        }
        const uniq = new Map();
        for (const x of days)
            uniq.set(x.ymd, x);
        return Array.from(uniq.values()).sort((a, b) => a.ymd.localeCompare(b.ymd));
    }, [displayIndexByCode, filterAtDate]);
    // [ADD][ANCHOR:MEMO:ALWAYS_COUNTS]
    const alwaysCounts = useMemo(() => {
        let gCount = 0;
        let iCount = 0;
        for (const code of Object.keys(displayIndexByCode)) {
            const idx = displayIndexByCode[code];
            const gr = idx?.groupRow;
            if (isEnabledRow(gr) && !isRangeTime(gr?.time))
                gCount += 1;
            const infos = Array.isArray(idx?.infoRows) ? idx.infoRows : [];
            for (const r of infos) {
                if (isEnabledRow(r) && !isRangeTime(r?.time))
                    iCount += 1;
            }
        }
        return { gCount, iCount };
    }, [displayIndexByCode]);
    // [ADD][ANCHOR:MEMO:FILTERED_INFO_BY_CODE]
    const filteredInfoByCode = useMemo(() => {
        const out = {};
        if (!filterAtDate)
            return out;
        for (const code of Object.keys(displayIndexByCode)) {
            const rows = displayIndexByCode[code]?.infoRows || [];
            const actives = rows.filter((r) => isEnabledRow(r) && isActiveAt(r?.time, filterAtDate));
            if (actives.length)
                out[code] = actives;
        }
        return out;
    }, [displayIndexByCode, filterAtDate]);
    const filteredInfoTotal = useMemo(() => {
        let n = 0;
        for (const rows of Object.values(filteredInfoByCode))
            n += (rows?.length || 0);
        return n;
    }, [filteredInfoByCode]);
    return (<div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-bold">Table Groups</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={openStandaloneInfoCreate} className="px-3 py-2 rounded bg-amber-500 text-black font-semibold hover:bg-amber-400 disabled:opacity-60" disabled={infoEditSaving}>{t("+ 인포버튼 단독 생성")}</button>
          <button onClick={fetchGroups} className="px-3 py-2 rounded bg-gray-800 text-white hover:bg-gray-700" disabled={loading}>{t("\uC0C8\uB85C\uACE0\uCE68")}</button>
        </div>
      </div>

      {error ? (<div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>) : null}

      {/* ✅ 달력/시간 필터 */}
      <div className="p-4 rounded border bg-white flex flex-wrap items-center gap-3">
        <div className="font-semibold">{t("\uD45C\uC2DC \uAE30\uC900 \uC2DC\uAC04")}</div>
        <input className="border rounded px-3 py-2" type="datetime-local" value={filterAtLocal} onChange={(e) => setFilterAtLocal(e.target.value)}/>
        <div className="text-sm text-gray-600">{t("* \uC774 \uC2DC\uAC04\uC5D0 \u201C\uC720\uD6A8\uD55C(display_sets.time)\u201D \uADF8\uB8F9\uB9CC \uC544\uB798 \uBAA9\uB85D\uC5D0 \uD45C\uC2DC")}</div>
      </div>

      {/* 그룹 생성 폼 */}
      <form onSubmit={onCreateGroup} className="p-4 rounded border bg-white space-y-4">
        <div className="font-semibold">{t("\uADF8\uB8F9 \uC0DD\uC131")}</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("\uADF8\uB8F9\uBA85")}</span>
            <input className="border rounded px-3 py-2" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={t("\uC608: VIP NLH 1/2")}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">SB</span>
            <input className="border rounded px-3 py-2" type="number" step="0.01" min="0.01" value={form.small_blind} onChange={(e) => setForm((p) => ({ ...p, small_blind: e.target.value }))}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">BB</span>
            <input className="border rounded px-3 py-2" type="number" step="0.01" min="0.01" value={form.big_blind} onChange={(e) => setForm((p) => ({ ...p, big_blind: e.target.value }))}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Default Game</span>
            <select className="border rounded px-3 py-2" value={form.default_game} onChange={(e) => setForm((p) => ({ ...p, default_game: e.target.value }))}>
              <option value="NLH">NLH</option>
              <option value="ALLBLIND">ALLBLIND</option>
              <option value="DONATIONBLIND">DONATIONBLIND</option>
              <option value="ONETIME">ONETIME</option>
              <option value="ALLIN25">ALLIN25</option>
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Max Players</span>
            <input className="border rounded px-3 py-2" type="number" value={form.max_players} onChange={(e) => setForm((p) => ({ ...p, max_players: e.target.value }))}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Max Count (sub tables)</span>
            <input className="border rounded px-3 py-2" type="number" value={form.max_count} onChange={(e) => setForm((p) => ({ ...p, max_count: e.target.value }))}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Lock</span>
            <select className="border rounded px-3 py-2" value={form.lock} onChange={(e) => setForm((p) => ({ ...p, lock: e.target.value }))}>
              <option value="UNLOCK">UNLOCK</option>
              <option value="LOCK">LOCK</option>
            </select>
          </label>

          {form.lock === 'LOCK' ? (<label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">Password</span>
              <input className="border rounded px-3 py-2" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder={t("\uC7A0\uAE08 \uBE44\uBC00\uBC88\uD638")}/>
            </label>) : null}

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Rake % (0~100)</span>
            <input className="border rounded px-3 py-2" type="number" min="0" max="100" step="0.0001" value={form.rake_percent} onChange={(e) => setForm((p) => ({ ...p, rake_percent: e.target.value }))}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Rake Cap (BB)</span>
            <input className="border rounded px-3 py-2" type="number" min="0" step="0.0001" value={form.rake_cap_bb} onChange={(e) => setForm((p) => ({ ...p, rake_cap_bb: e.target.value }))} placeholder="15"/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">Event Fee % (0~100)</span>
            <input className="border rounded px-3 py-2" type="number" min="0" max="100" step="0.0001" value={form.event_fee_percent} onChange={(e) => setForm((p) => ({ ...p, event_fee_percent: e.target.value }))} placeholder="1"/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("\uB77C\uC6B4\uC9C0 \uD3EC\uC778\uD2B8 % (0~100)")}</span>
            <input className="border rounded px-3 py-2" type="number" min="0" max="100" step="0.01" value={form.lounge_point_percent} onChange={(e) => setForm((p) => ({ ...p, lounge_point_percent: e.target.value }))} placeholder="0.25"/>
          </label>

          
        </div>

        {/* ✅ 그룹 DisplaySet 설정 */}
        <div className="border rounded p-3">
          <div className="font-semibold mb-3">{t("\uADF8\uB8F9 \uB178\uCD9C(DisplaySet)")}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">{t("\uD558\uC774\uB77C\uC774\uD2B8 \uD3EC\uC778\uD2B8")}</span>
              <input className="border rounded px-3 py-2" value={form.highlight_point} onChange={(e) => setForm((p) => ({ ...p, highlight_point: e.target.value }))} placeholder={t("\uC608: HOT")}/>
            </label>

            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-sm text-gray-600">{t("\uD558\uC774\uB77C\uC774\uD2B8 \uBA54\uC138\uC9C0")}</span>
              <input className="border rounded px-3 py-2" value={form.highlight_message} onChange={(e) => setForm((p) => ({ ...p, highlight_message: e.target.value }))} placeholder={t("\uC608: \uC624\uB298\uB9CC \uB808\uC774\uD06C 0%")}/>
            </label>

            <div className="md:col-span-3 border rounded p-3">
              <div className="font-medium mb-2">{t("\uC2DC\uAC04 \uC124\uC815")}</div>
              <div className="flex gap-3 items-center flex-wrap">
                <label className="flex items-center gap-2">
                  <input type="radio" checked={form.group_time_mode === 'range'} onChange={() => setForm((p) => ({ ...p, group_time_mode: 'range' }))}/>{t("\uAE30\uAC04")}</label>
                <label className="flex items-center gap-2">
                  <input type="radio" checked={form.group_time_mode === 'infinite'} onChange={() => setForm((p) => ({ ...p, group_time_mode: 'infinite' }))}/>{t("\uBB34\uD55C(30\uC77C \uAC31\uC2E0)")}</label>
              </div>

              {form.group_time_mode === 'range' ? (<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">{t("\uC2DC\uC791(\uB85C\uCEEC)")}</span>
                    <input className="border rounded px-3 py-2" type="datetime-local" value={form.group_start_local} onChange={(e) => setForm((p) => ({ ...p, group_start_local: e.target.value }))}/>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">{t("\uC885\uB8CC(\uB85C\uCEEC)")}</span>
                    <input className="border rounded px-3 py-2" type="datetime-local" value={form.group_end_local} onChange={(e) => setForm((p) => ({ ...p, group_end_local: e.target.value }))}/>
                  </label>
                </div>) : (<div className="mt-3">
                  <label className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{t("\uAC31\uC2E0 \uC8FC\uAE30(\uC77C)")}</span>
                    <input className="border rounded px-2 py-1 w-24" type="number" min="1" value={form.group_refresh_days} onChange={(e) => setForm((p) => ({ ...p, group_refresh_days: e.target.value }))}/>
                  </label>
                </div>)}
            </div>
          </div>
        </div>

        {/* ✅ 인포 버튼 프리셋 */}
        <div className="border rounded p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold">{t("\uC778\uD3EC\uBA54\uC774\uC158 \uBC84\uD2BC(\uC120\uD0DD)")}</div>
            <button type="button" onClick={addInfoButton} className="px-3 py-2 rounded border hover:bg-gray-50">{t("+ \uC778\uD3EC \uBC84\uD2BC \uCD94\uAC00")}</button>
          </div>

          {(form.info_buttons || []).length ? (<div className="space-y-3">
              {form.info_buttons.map((b, idx) => (<InfoPresetBuilder key={idx} title={`Info Button #${idx + 1}`} value={b} onChange={(next) => updateInfoButton(idx, next)} allowRemove onRemove={() => removeInfoButton(idx)}/>))}
            </div>) : (<div className="text-sm text-gray-600">{t("* \uD544\uC694\uD560 \uB54C\uB9CC \uCD94\uAC00\uD558\uBA74 \uB428")}</div>)}
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={!canSubmit || creating} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60">
            {creating ? t("\uC0DD\uC131 \uC911...") : t("\uADF8\uB8F9 \uC0DD\uC131")}
          </button>

          <button type="button" onClick={() => setForm(DEFAULT_FORM)} className="px-4 py-2 rounded border">{t("\uCD08\uAE30\uD654")}</button>
        </div>
      </form>

      {/* 목록 */}
      <div className="p-4 rounded border bg-white">
       {/* [ADD][ANCHOR:UI:DISPLAYSETS_DAY_FILTER] */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="font-semibold">{t("DisplaySet \uB0A0\uC9DC \uD544\uD130(1\uC77C \uB2E8\uC704)")}</div>
            <button type="button" className="px-3 py-1.5 rounded border hover:bg-gray-50" onClick={() => {
            setQuickDayYmd('');
            setFilterAtLocal(nowLocalInput());
        }}>{t("\uC9C0\uAE08")}</button>
          </div>

         <div className="flex flex-wrap gap-2">
            {/* ✅ 상시(infinite) 항목이 있으면 날짜 버튼 대신 1개로 표시 */}
            {(alwaysCounts.gCount > 0 || alwaysCounts.iCount > 0) ? (<button type="button" onClick={() => {
                setQuickDayYmd('__ALWAYS__');
                setFilterAtLocal(nowLocalInput());
            }} className={quickDayYmd === '__ALWAYS__'
                ? 'px-3 py-1.5 rounded bg-gray-800 text-white'
                : 'px-3 py-1.5 rounded border hover:bg-gray-50'} title={t("\uBB34\uD55C(infinite) \uD56D\uBAA9(\uC0C1\uC2DC \uB178\uCD9C)")}>{t("\uC0C1\uC2DC")}<span className="opacity-80">(G{alwaysCounts.gCount}/I{alwaysCounts.iCount})</span>
              </button>) : null}

            {dayButtons.map((d) => {
            const active = quickDayYmd === d.ymd;
            return (<button key={d.ymd} type="button" onClick={() => {
                    setQuickDayYmd(d.ymd);
                    setFilterAtLocal(ymdToFilterLocalInput(d.ymd));
                }} className={active
                    ? 'px-3 py-1.5 rounded bg-gray-800 text-white'
                    : 'px-3 py-1.5 rounded border hover:bg-gray-50'} title={t("\uD074\uB9AD\uD558\uBA74 \uD45C\uC2DC \uAE30\uC900 \uC2DC\uAC04\uC774 \uD574\uB2F9 \uB0A0\uC9DC\uB85C \uC124\uC815\uB429\uB2C8\uB2E4")}>
                  {d.ymd} <span className="opacity-80">(G{d.gCount}/I{d.iCount})</span>
                </button>);
        })}
          </div>
         {(dayButtons.length === 0) ? (<div className="text-sm text-gray-600">{t("* \uD604\uC7AC \uAE30\uC900\uC2DC\uAC04 \uC8FC\uBCC0(\u00B115~45\uC77C)\uC5D0 \uAE30\uAC04(range) \uC124\uC815\uB41C DisplaySet\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.")}</div>) : null}
        </div>
        

        {/* [ADD][ANCHOR:UI:INFO_PREVIEW_LIST] */}
        {filteredInfoTotal > 0 ? (<div className="mb-4 border rounded p-3 bg-gray-50">
            <div className="font-semibold mb-2">{t("\uC720\uD6A8 \uC778\uD3EC\uBA54\uC774\uC158 \uBC84\uD2BC:")}{filteredInfoTotal}{t("\uAC1C")}</div>
            <div className="space-y-2">
              {Object.entries(filteredInfoByCode).map(([code, rows]) => {
                const isStandalone = standaloneDisplayCodes.includes(code);
                const groupName = isStandalone
                    ? `${t('단독 인포버튼')} · ${code}`
                    : (toI18nText((tableGroups || []).find((g) => g.display_code === code)?.name, 'ko') || code);
                return (<div key={code} className="border rounded bg-white p-2">
                    <div className="font-medium mb-1">{toI18nText(groupName, 'ko') || code}</div>
                    <div className="space-y-1">
                      {(rows || []).map((r) => (<div key={r.id} className="text-sm flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <span className="font-semibold">{toI18nText(r.highlight_point, 'ko') || '-'}</span>
                            <span className="mx-2 text-gray-400">|</span>
                            <span>{toI18nText(r.highlight_message, 'ko') || '-'}</span>
                            <div className="text-xs text-gray-500 mt-0.5">
                              time: {formatTimeHuman(normalizeTimeObj(r?.time))}
                            </div>
                            <div className="text-xs text-blue-700 mt-0.5">
                              target: {r.device || 'all'} / {r.language || 'all'} / {r.country || 'ALL'} · priority: {r.priority ?? 0}
                            </div>
                          </div>
                          <div className="shrink-0 flex items-center gap-2">
                            <button type="button" className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60" disabled={infoEditSaving} onClick={() => openInfoPresetEdit(code, groupName, r)}>{t("\uD504\uB9AC\uC14B \uC218\uC815")}</button>
                            <button type="button" className="px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60" disabled={infoEditSaving} onClick={() => deleteInfoCard(code, r, groupName)}>{t("\uC0AD\uC81C")}</button>
                          </div>
                        </div>))}
                    </div>
                  </div>);
            })}
            </div>
          </div>) : null}
        {loading ? (<div className="text-gray-600">{t("\uB85C\uB529\uC911...")}</div>) : (<GameList tableGroups={filteredGroups} onNeedRefresh={fetchGroups} displayMetaByCode={displayMetaByCode} displaySetsByCode={displaySetsByCode}/>)}
    {/* [ADD][ANCHOR:UI:INFO_PRESET_EDIT_MODAL] */}
      {infoEditOpen ? (<div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center overflow-y-auto p-4">
          <div className="w-full max-w-[1680px] bg-white rounded shadow-lg overflow-auto max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold">{infoEditTarget?.mode === 'standalone-create' ? t("인포버튼 단독 생성") : t("\uC778\uD3EC \uBC84\uD2BC \uD504\uB9AC\uC14B \uC218\uC815")}<span className="ml-2 text-xs text-gray-500">
                  ({infoEditTarget?.groupName || infoEditTarget?.code || '-'})
                </span>
              </div>
              <button type="button" className="px-3 py-1.5 rounded border hover:bg-gray-50" onClick={() => setInfoEditOpen(false)} disabled={infoEditSaving}>{t("\uB2EB\uAE30")}</button>
            </div>

            <div className="p-4">
              {renderInfoTargetVariantMatrix()}
              <InfoPresetBuilder title="Information Button" value={infoEditDraft} onChange={setInfoEditDraft} enableAdvancedDesign/>
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t bg-gray-50">
              {infoEditTarget?.mode === 'standalone-create' ? <span /> : (<button type="button" className="px-4 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60" onClick={() => deleteInfoCard(infoEditTarget?.code, { id: infoEditTarget?.id, highlight_point: infoEditTarget?.hp }, infoEditTarget?.groupName)} disabled={infoEditSaving}>{t("\uC778\uD3EC\uCE74\uB4DC \uC0AD\uC81C")}</button>)}
              <div className="flex justify-end gap-2">
                <button type="button" className="px-4 py-2 rounded border hover:bg-gray-50" onClick={() => setInfoEditOpen(false)} disabled={infoEditSaving}>{t("\uCDE8\uC18C")}</button>
                <button type="button" className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60" onClick={saveInfoPresetEdit} disabled={infoEditSaving}>
                  {infoEditSaving ? t("\uC800\uC7A5 \uC911...") : (infoEditTarget?.mode === 'standalone-create' ? t("생성") : t("\uC800\uC7A5"))}
                </button>
              </div>
            </div>
          </div>
        </div>) : null}
      </div>
    </div>);
}
